/**
 * Contracts & commercial selectors — contracts, VOs, IPCs, claims,
 * correspondence, profitability, RFQ/RFP/PO.
 */

import type {
  Claim,
  Contract,
  CurrencyCode,
  IsoDate,
  MockState,
  Project,
  VariationOrder,
} from '../types';
import { isVoApproved } from '../voStatus';

// ---- Project profitability (FR-086) -------------------------------------

export interface ProfitabilityView {
  projectId: string;
  originalContractValue: number;
  approvedVOValue: number;
  settledClaimValue: number;
  actualCost: number;
  derivedTotalRevenue: number;
  derivedMargin: number;
  derivedMarginPct: number;
}

export function selectProjectProfitability(state: MockState, projectId: string): ProfitabilityView {
  const contracts = state.contracts.filter(
    (c: Contract) => c.projectId === projectId && c.type === 'client',
  );
  const original = contracts.reduce((acc, c) => acc + c.originalValue, 0);

  const approvedVOs = state.variationOrders.filter(
    (v: VariationOrder) => v.projectId === projectId && isVoApproved(v.status),
  );
  const voValue = approvedVOs.reduce((acc, v) => acc + v.valueImpact, 0);

  const settledClaims = state.claims.filter(
    (cl: Claim) => cl.projectId === projectId && cl.status === 'settled',
  );
  const claimValue = settledClaims.reduce((acc, cl) => acc + (cl.awardedValue ?? 0), 0);

  const budgets = state.budgets.filter((b) => b.projectId === projectId);
  const actualCost = budgets.reduce((acc, b) => acc + b.actual, 0);

  const derivedTotalRevenue = original + voValue + claimValue;
  const derivedMargin = derivedTotalRevenue - actualCost;
  const derivedMarginPct = derivedTotalRevenue === 0 ? 0 : derivedMargin / derivedTotalRevenue;

  return {
    projectId,
    originalContractValue: original,
    approvedVOValue: voValue,
    settledClaimValue: claimValue,
    actualCost,
    derivedTotalRevenue,
    derivedMargin,
    derivedMarginPct,
  };
}

export function selectPortfolioMarginPct(state: MockState): number {
  if (state.projects.length === 0) return 0;
  const projects = state.projects.filter(
    (p) => p.phase === 'execution' || p.phase === 'closeOut',
  );
  if (projects.length === 0) return 0;
  const sum = projects.reduce(
    (acc, p) => acc + selectProjectProfitability(state, p.id).derivedMarginPct,
    0,
  );
  return sum / projects.length;
}

export function selectTotalContractValue(state: MockState): number {
  return state.contracts
    .filter((c) => c.type === 'client')
    .reduce((acc, c) => acc + c.originalValue, 0);
}

// ---- Contracts enrichment (FR-080, FR-081) ------------------------------

export interface EnrichedContractRow {
  id: string;
  code: string;
  type: 'client' | 'subcontract';
  projectId: string;
  projectCode: string;
  projectName: string;
  clientName: string;
  counterpartyName: string;
  originalValue: number;
  approvedVOValue: number;
  currentValue: number;
  currency: CurrencyCode;
  startDate: IsoDate;
  endDate: IsoDate;
  paymentTerms: string;
  status: Contract['status'];
  voCount: number;
  ipcCount: number;
  claimsCount: number;
}

export function selectEnrichedContracts(
  state: MockState,
  kind?: 'client' | 'subcontract',
): EnrichedContractRow[] {
  return state.contracts
    .filter((c) => !kind || c.type === kind)
    .map((c) => {
      const proj = state.projects.find((p) => p.id === c.projectId);
      const client = state.clients.find((cl) => cl.id === c.clientId);
      const sup = c.subcontractorId
        ? state.suppliers.find((s) => s.id === c.subcontractorId)
        : undefined;
      const approvedVOs = state.variationOrders.filter(
        (v) => v.contractId === c.id && isVoApproved(v.status),
      );
      const approvedVOValue = approvedVOs.reduce((a, v) => a + v.valueImpact, 0);
      const voCount = state.variationOrders.filter((v) => v.contractId === c.id).length;
      const ipcCount = state.ipcs.filter((i) => i.contractId === c.id).length;
      const claimsCount = state.claims.filter((cl) => cl.contractId === c.id).length;
      return {
        id: c.id,
        code: c.code,
        type: c.type,
        projectId: c.projectId,
        projectCode: proj?.code ?? '—',
        projectName: proj?.name ?? '—',
        clientName: client?.name ?? '—',
        counterpartyName: c.type === 'client' ? (client?.name ?? '—') : (sup?.name ?? '—'),
        originalValue: c.originalValue,
        approvedVOValue,
        currentValue: c.originalValue + approvedVOValue,
        currency: c.currency,
        startDate: c.startDate,
        endDate: c.endDate,
        paymentTerms: c.paymentTerms,
        status: c.status,
        voCount,
        ipcCount,
        claimsCount,
      };
    });
}

// ---- IPC list (FR-082) --------------------------------------------------

export interface IpcListRow {
  id: string;
  contractId: string;
  contractCode: string;
  projectCode: string;
  cycleNumber: number;
  cumulativeValue: number;
  retentionAmount: number;
  vatAmount: number;
  netAmount: number;
  status: 'draft' | 'submitted' | 'approved' | 'paid';
  issuedDate: IsoDate;
}

export function selectIpcs(state: MockState, contractId?: string): IpcListRow[] {
  return state.ipcs
    .filter((i) => !contractId || i.contractId === contractId)
    .map((ipc) => {
      const ct = state.contracts.find((c) => c.id === ipc.contractId);
      const proj = ct ? state.projects.find((p) => p.id === ct.projectId) : undefined;
      return {
        id: ipc.id,
        contractId: ipc.contractId,
        contractCode: ct?.code ?? '—',
        projectCode: proj?.code ?? '—',
        cycleNumber: ipc.cycleNumber,
        cumulativeValue: ipc.cumulativeValue,
        retentionAmount: ipc.retentionAmount,
        vatAmount: ipc.vatAmount,
        netAmount: ipc.netAmount,
        status: ipc.status,
        issuedDate: ipc.issuedDate,
      };
    });
}

// ---- VO register (FR-083) ------------------------------------------------

export interface VoListRow {
  id: string;
  code: string;
  title: string;
  contractId: string;
  contractCode: string;
  projectCode: string;
  projectName: string;
  valueImpact: number;
  marginImpact: number;
  status: VariationOrder['status'];
  createdAt: IsoDate;
}

export function selectVoList(state: MockState): VoListRow[] {
  return state.variationOrders.map((v) => {
    const ct = state.contracts.find((c) => c.id === v.contractId);
    const proj = state.projects.find((p) => p.id === v.projectId);
    return {
      id: v.id,
      code: v.code,
      title: v.title,
      contractId: v.contractId,
      contractCode: ct?.code ?? '—',
      projectCode: proj?.code ?? '—',
      projectName: proj?.name ?? '—',
      valueImpact: v.valueImpact,
      marginImpact: v.marginImpact,
      status: v.status,
      createdAt: v.createdAt,
    };
  });
}

/** VO status distribution (dashboard chart). */
export interface VOStatusRow {
  status: VariationOrder['status'];
  count: number;
  value: number;
}

export function selectVOsByStatus(state: MockState): VOStatusRow[] {
  const acc = new Map<VariationOrder['status'], VOStatusRow>();
  for (const vo of state.variationOrders) {
    const current = acc.get(vo.status) ?? { status: vo.status, count: 0, value: 0 };
    current.count += 1;
    current.value += vo.valueImpact;
    acc.set(vo.status, current);
  }
  return Array.from(acc.values());
}

/** VO value impact by project — top N projects by summed VO value. */
export interface VOImpactRow {
  projectId: string;
  projectName: string;
  value: number;
}

export function selectVOImpactByProject(state: MockState, topN = 10): VOImpactRow[] {
  const byProject = new Map<string, number>();
  for (const vo of state.variationOrders) {
    byProject.set(vo.projectId, (byProject.get(vo.projectId) ?? 0) + vo.valueImpact);
  }
  const rows: VOImpactRow[] = [];
  for (const [projectId, value] of byProject.entries()) {
    const project = state.projects.find((p) => p.id === projectId);
    rows.push({ projectId, projectName: project?.code ?? projectId, value });
  }
  rows.sort((a, b) => b.value - a.value);
  return rows.slice(0, topN);
}

/** Approved VOs total value (clientApproved | posted | recomputed). */
export function selectApprovedVOValue(state: MockState): number {
  return state.variationOrders
    .filter((v) => isVoApproved(v.status))
    .reduce((acc, v) => acc + v.valueImpact, 0);
}

// ---- Claims register (FR-084) -------------------------------------------

/** Claims aging by status — plain counts + value stacks (dashboard chart). */
export interface ClaimStatusRow {
  status: Claim['status'];
  count: number;
  value: number;
}

export function selectClaimsByStatus(state: MockState): ClaimStatusRow[] {
  const acc = new Map<Claim['status'], ClaimStatusRow>();
  for (const cl of state.claims) {
    const current = acc.get(cl.status) ?? { status: cl.status, count: 0, value: 0 };
    current.count += 1;
    current.value += cl.claimedValue;
    acc.set(cl.status, current);
  }
  return Array.from(acc.values());
}

/** Claim value vs awarded — top N claims sorted by claimed value. */
export interface ClaimVsAwardedRow {
  code: string;
  claimedValue: number;
  awardedValue: number;
}

export function selectClaimVsAwarded(state: MockState, topN = 10): ClaimVsAwardedRow[] {
  return state.claims
    .slice()
    .sort((a, b) => b.claimedValue - a.claimedValue)
    .slice(0, topN)
    .map((cl) => ({
      code: cl.code,
      claimedValue: cl.claimedValue,
      awardedValue: cl.awardedValue ?? 0,
    }));
}

/** Enriched claim list for the S2-T23 register page. */
export interface ClaimListRow {
  id: string;
  code: string;
  contractId: string;
  contractCode: string;
  projectId: string;
  projectCode: string;
  projectName: string;
  clientName: string;
  claimedValue: number;
  awardedValue: number | null;
  status: Claim['status'];
  createdAt: IsoDate;
  currency: CurrencyCode;
}

export function selectClaimsEnriched(state: MockState): ClaimListRow[] {
  return state.claims.map((cl) => {
    const ct = state.contracts.find((c) => c.id === cl.contractId);
    const proj = state.projects.find((p) => p.id === cl.projectId);
    const client = proj ? state.clients.find((c) => c.id === proj.clientId) : undefined;
    return {
      id: cl.id,
      code: cl.code,
      contractId: cl.contractId,
      contractCode: ct?.code ?? '—',
      projectId: cl.projectId,
      projectCode: proj?.code ?? '—',
      projectName: proj?.name ?? '—',
      clientName: client?.name ?? '—',
      claimedValue: cl.claimedValue,
      awardedValue: cl.awardedValue ?? null,
      status: cl.status,
      createdAt: cl.createdAt,
      currency: ct?.currency ?? proj?.currency ?? 'SAR',
    };
  });
}

// ---- Commercial correspondence (FR-085) ---------------------------------

export interface CorrespondenceListRow {
  id: string;
  code: string;
  category: 'letter' | 'rfi' | 'notice' | 'objection';
  subject: string;
  direction: 'inbound' | 'outbound';
  counterparty: string;
  status: 'draft' | 'sent' | 'received' | 'acknowledged' | 'closed';
  issuedDate: IsoDate;
  scope: 'contract' | 'generic';
  contractId: string | null;
  contractCode: string | null;
  relatedVoCode: string | null;
  relatedClaimCode: string | null;
}

export function selectCorrespondence(state: MockState): CorrespondenceListRow[] {
  return state.correspondence.map((c) => {
    const ct = c.contractId ? state.contracts.find((x) => x.id === c.contractId) : undefined;
    const vo = c.relatedVoId
      ? state.variationOrders.find((x) => x.id === c.relatedVoId)
      : undefined;
    const cl = c.relatedClaimId
      ? state.claims.find((x) => x.id === c.relatedClaimId)
      : undefined;
    return {
      id: c.id,
      code: c.code,
      category: c.category,
      subject: c.subject,
      direction: c.direction,
      counterparty: c.counterparty,
      status: c.status,
      issuedDate: c.issuedDate,
      scope: c.scope,
      contractId: ct?.id ?? null,
      contractCode: ct?.code ?? null,
      relatedVoCode: vo?.code ?? null,
      relatedClaimCode: cl?.code ?? null,
    };
  });
}

// ---- Project profitability list (FR-086) --------------------------------

export interface ProjectProfitabilityRow {
  projectId: string;
  projectCode: string;
  projectName: string;
  clientName: string;
  originalContractValue: number;
  approvedVOValue: number;
  settledClaimValue: number;
  derivedTotalRevenue: number;
  actualCost: number;
  derivedMargin: number;
  derivedMarginPct: number;
  currency: CurrencyCode;
}

export function selectProjectProfitabilityList(state: MockState): ProjectProfitabilityRow[] {
  return state.projects.map((p) => {
    const prof = selectProjectProfitability(state, p.id);
    const client = state.clients.find((c) => c.id === p.clientId);
    return {
      projectId: p.id,
      projectCode: p.code,
      projectName: p.name,
      clientName: client?.name ?? '—',
      originalContractValue: prof.originalContractValue,
      approvedVOValue: prof.approvedVOValue,
      settledClaimValue: prof.settledClaimValue,
      derivedTotalRevenue: prof.derivedTotalRevenue,
      actualCost: prof.actualCost,
      derivedMargin: prof.derivedMargin,
      derivedMarginPct: prof.derivedMarginPct,
      currency: p.currency,
    };
  });
}

// ---- RFQ / RFP / PO enrichment (FR-087..089) ----------------------------

export interface RfqListRow {
  id: string;
  title: string;
  projectId: string;
  projectCode: string;
  costCode: string;
  status: 'draft' | 'sent' | 'repliesReceived' | 'awarded';
  issuedDate: IsoDate;
}

export function selectRfqList(state: MockState): RfqListRow[] {
  return state.rfqs.map((r) => {
    const proj = state.projects.find((p) => p.id === r.projectId);
    const cc = state.costCodes.find((c) => c.id === r.costCodeId);
    return {
      id: r.id,
      title: r.title,
      projectId: r.projectId,
      projectCode: proj?.code ?? '—',
      costCode: cc?.code ?? '—',
      status: r.status,
      issuedDate: r.issuedDate,
    };
  });
}

export interface RfpListRow {
  id: string;
  title: string;
  projectId: string;
  projectCode: string;
  costCode: string;
  status: 'draft' | 'sent' | 'repliesReceived' | 'awarded';
  issuedDate: IsoDate;
}

export function selectRfpList(state: MockState): RfpListRow[] {
  return state.rfps.map((r) => {
    const proj = state.projects.find((p) => p.id === r.projectId);
    const cc = state.costCodes.find((c) => c.id === r.costCodeId);
    return {
      id: r.id,
      title: r.title,
      projectId: r.projectId,
      projectCode: proj?.code ?? '—',
      costCode: cc?.code ?? '—',
      status: r.status,
      issuedDate: r.issuedDate,
    };
  });
}

export interface PoListRow {
  id: string;
  code: string;
  projectId: string;
  projectCode: string;
  supplierName: string;
  costCode: string;
  costCodeNameEn: string;
  costCodeNameAr: string;
  totalValue: number;
  currency: CurrencyCode;
  status: 'draft' | 'approved' | 'issued' | 'partialReceipt' | 'closed';
  issuedDate: IsoDate;
}

export function selectPoList(state: MockState): PoListRow[] {
  return state.purchaseOrders.map((po) => {
    const proj = state.projects.find((p) => p.id === po.projectId);
    const sup = state.suppliers.find((s) => s.id === po.supplierId);
    const cc = state.costCodes.find((c) => c.id === po.costCodeId);
    return {
      id: po.id,
      code: po.code,
      projectId: po.projectId,
      projectCode: proj?.code ?? '—',
      supplierName: sup?.name ?? '—',
      costCode: cc?.code ?? '—',
      costCodeNameEn: cc?.nameEn ?? '—',
      costCodeNameAr: cc?.nameAr ?? '—',
      totalValue: po.totalValue,
      currency: po.currency,
      status: po.status,
      issuedDate: po.issuedDate,
    };
  });
}

/** Purchase-order value by project — top N (dashboard chart). */
export interface POValueRow {
  projectId: string;
  projectCode: string;
  value: number;
}

export function selectPOValueByProject(state: MockState, topN = 8): POValueRow[] {
  const acc = new Map<string, number>();
  for (const po of state.purchaseOrders) {
    acc.set(po.projectId, (acc.get(po.projectId) ?? 0) + po.totalValue);
  }
  const rows: POValueRow[] = [];
  for (const [projectId, value] of acc.entries()) {
    const p: Project | undefined = state.projects.find((x) => x.id === projectId);
    rows.push({ projectId, projectCode: p?.code ?? projectId, value });
  }
  rows.sort((a, b) => b.value - a.value);
  return rows.slice(0, topN);
}

/** Contracts expiring within N days. */
export function selectContractsExpiring(state: MockState, withinDays = 90): number {
  const today = new Date('2026-08-31');
  const cutoff = new Date(today);
  cutoff.setDate(cutoff.getDate() + withinDays);
  const cutoffIso = cutoff.toISOString().slice(0, 10);
  return state.contracts.filter(
    (c) =>
      c.status === 'active' &&
      c.endDate >= today.toISOString().slice(0, 10) &&
      c.endDate <= cutoffIso,
  ).length;
}
